import React, {useState, useEffect} from 'react';
import {Modal, Input, Button, LoadingSpinner, Checkbox} from '@/components/ui';
import {useGameInstances} from '@/hooks/useGameInstances';
import type {GameHost, Account, CreateGameHostData, UpdateGameHostData} from '@/types';

interface CreateHostModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: CreateGameHostData | UpdateGameHostData) => void;
    editingHost?: GameHost | null;
    fixedAccountId?: string; // For AccountDetail - locks to specific account
    accounts?: Account[]; // For ManageGameHost - provides dropdown options
    isLoading?: boolean;
    hostSingular?: string; // "Game Host" or "Teacher" etc.
}

interface FormData {
    account_id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    send_invitation: boolean;
    game_access: string[];
}

export const CreateHostModal: React.FC<CreateHostModalProps> = ({
                                                                    isOpen,
                                                                    onClose,
                                                                    onSubmit,
                                                                    editingHost,
                                                                    fixedAccountId,
                                                                    accounts = [],
                                                                    isLoading = false,
                                                                    hostSingular = 'Game Host'
                                                                }) => {
    const {data: gameInstances = [], isLoading: gameInstancesLoading} = useGameInstances();

    const initialFormData: FormData = {
        account_id: fixedAccountId || '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        send_invitation: true,
        game_access: []
    };

    const [formData, setFormData] = useState<FormData>(initialFormData);

    // Reset form when modal opens/closes or editing host changes
    useEffect(() => {
        if (editingHost) {
            setFormData({
                account_id: editingHost.account_id,
                first_name: editingHost.first_name,
                last_name: editingHost.last_name,
                email: editingHost.email,
                phone: editingHost.phone || '',
                send_invitation: false,
                game_access: [] // Could be populated from existing permissions if needed
            });
        } else {
            setFormData(initialFormData);
        }
    }, [editingHost, fixedAccountId, isOpen]);

    const handleInputChange = (field: keyof FormData, value: string | boolean | string[]): void => {
        // Normalize email to lowercase
        const normalizedValue = field === 'email' && typeof value === 'string'
            ? value.toLowerCase()
            : value;

        setFormData(prev => ({...prev, [field]: normalizedValue}));
    };

    const handleGameAccessChange = (gameName: string, checked: boolean): void => {
        setFormData(prev => ({
            ...prev,
            game_access: checked
                ? [...prev.game_access, gameName]
                : prev.game_access.filter(name => name !== gameName)
        }));
    };

    const handleSubmit = (e: React.FormEvent): void => {
        e.preventDefault();

        // Find the selected account to get its account_type
        const selectedAccount: Account | undefined = accounts.find(acc => acc.id === formData.account_id);
        const account_type = selectedAccount?.account_type || 'academic'; // Use account's type, fallback to academic
        const phone = formData.phone.trim() || undefined;

        if (editingHost) {
            // Edit updates the game_hosts row directly, so send ONLY real columns.
            // send_invitation and game_access are create-time concerns (invitation
            // flow); game access for existing hosts lives on the permissions page.
            const updateData: UpdateGameHostData = {
                account_id: formData.account_id,
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                phone,
                account_type,
            };
            onSubmit(updateData);
            return;
        }

        const createData: CreateGameHostData = {
            ...formData,
            account_type,
            phone,
        };
        onSubmit(createData);
    };

    const accountOptions = accounts.map(acc => ({
        value: acc.id,
        label: acc.name
    }));

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`${editingHost ? 'Edit' : 'Create'} ${hostSingular}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Selection - only show if not fixed */}
                {!fixedAccountId && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Account <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={formData.account_id}
                            onChange={(e) => handleInputChange('account_id', e.target.value)}
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select an account</option>
                            {accountOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            First Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.first_name}
                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                            placeholder="Enter first name"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Last Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            value={formData.last_name}
                            onChange={(e) => handleInputChange('last_name', e.target.value)}
                            placeholder="Enter last name"
                            required
                        />
                    </div>
                </div>

                {/* Contact Fields */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email <span className="text-red-500">*</span>
                        </label>
                        <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="Enter email address"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                        </label>
                        <Input
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="Enter phone number"
                        />
                    </div>
                </div>

                {/* Invitation Settings */}
                {!editingHost && (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Invitation Settings</h3>
                        <div className="flex items-center space-x-3">
                            <Checkbox
                                id="send_invitation"
                                checked={formData.send_invitation}
                                onChange={(e) => handleInputChange('send_invitation', e.target.checked)}
                            />
                            <label htmlFor="send_invitation" className="text-sm text-gray-700">
                                Send invitation email immediately after creating the {hostSingular.toLowerCase()}
                            </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            The {hostSingular.toLowerCase()} will receive an email with instructions to complete their
                            registration.
                        </p>
                    </div>
                )}

                {/* Game Access Settings */}
                {!editingHost && (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Game Access Permissions</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Select which games this {hostSingular.toLowerCase()} will have access to. Access can be
                            modified later.
                        </p>

                        {gameInstancesLoading ? (
                            <div className="flex items-center justify-center py-4">
                                <LoadingSpinner size="sm"/>
                                <span className="ml-2 text-gray-500">Loading games...</span>
                            </div>
                        ) : gameInstances.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-gray-500">No games available</p>
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
                                {gameInstances.map((game) => (
                                    <div key={game.id} className="flex items-start space-x-3">
                                        <Checkbox
                                            id={`game-${game.name}`}
                                            checked={formData.game_access.includes(game.name)}
                                            onChange={(e) => handleGameAccessChange(game.name, e.target.checked)}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <label
                                                htmlFor={`game-${game.name}`}
                                                className="block text-sm font-medium text-gray-900 cursor-pointer"
                                            >
                                                {game.display_name}
                                            </label>
                                            {game.description && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {game.description}
                                                </p>
                                            )}
                                        </div>
                                        {game.thumbnail && (
                                            <img
                                                src={game.thumbnail}
                                                alt={game.display_name}
                                                className="w-8 h-8 rounded object-cover"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <p className="text-xs text-gray-500 mt-2">
                            Selected games: {formData.game_access.length} of {gameInstances.length}
                        </p>
                    </div>
                )}

                {/* Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <Button type="button" variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" isLoading={isLoading}>
                        {editingHost ? `Update ${hostSingular}` : `Create ${hostSingular}`}
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
